import { isFocusable } from "@otto-ec/otto-components-utils/utils/focus/isFocusable";
import { isFocusableDeepWithin } from "@otto-ec/otto-components-utils/utils/focus/isFocusableDeepWithin";
import { isInApp } from "@otto-ec/otto-components-utils/utils/isInApp";
import { on } from "svelte/events";

export enum HeaderSkipLinkIdentifier {
  MAIN_NAVIGATION = "main-navigation",
  SUB_NAVIGATION = "sub-navigation",
  FILTERS = "filters",
  MAIN_CONTENT = "main-content",
}

type SkipLinkCandidate = {
  selector: string;
  secondarySelector?: string;
  title: string;
};

const SKIPLINK_CANDIDATES_SELECTOR: SkipLinkCandidate[] = [
  {
    selector: `[data-skiplink=${HeaderSkipLinkIdentifier.MAIN_NAVIGATION}]`,
    title: "Zur Hauptnavigation", /*                              */
  },
  {
    selector: `[data-skiplink=${HeaderSkipLinkIdentifier.SUB_NAVIGATION}]`,
    title: "Zur Seitennavigation",
  },
  {
    selector: `[data-skiplink=${HeaderSkipLinkIdentifier.FILTERS}]`,
    title: "Zu den Filtern",
  },
  {
    selector: `[data-skiplink=${HeaderSkipLinkIdentifier.MAIN_CONTENT}]`,
    secondarySelector: "main",
    title: "Zum Inhalt",
  },
];

const INJECTED_SKIPLINKS_CLASS = "injected-header-skiplinks";
const WAIT_FOR_APP_TIMEOUT_MS = 500;

const findSkiplink = (selector: string): HTMLElement | null => {
  const found = document.querySelector<HTMLElement>(selector);

  if (!found) return null;
  if (!found.offsetParent) return null;
  if (!isFocusable(found) && !isFocusableDeepWithin(found)) return null;

  return found;
};

const injectHeaderSkiplinks = async (reInject = false): Promise<void> => {
  if (
    /*                                            */
    await Promise.race([
      isInApp(),
      new Promise((resolve) => setTimeout(() => resolve(false), WAIT_FOR_APP_TIMEOUT_MS)),
    ])
  ) {
    /*                                      */
    return;
  }

  if (document.querySelector(`.${INJECTED_SKIPLINKS_CLASS}`)) {
    /*                          */
    if (!reInject) return;
    /*                                           */
    const existingSkipLinks = document.querySelector(`.${INJECTED_SKIPLINKS_CLASS}`);
    existingSkipLinks?.remove();
  }

  const existingSkipLinks = [];
  for (let i = 0; i < SKIPLINK_CANDIDATES_SELECTOR.length; i++) {
    const skiplink = SKIPLINK_CANDIDATES_SELECTOR[i];
    let usedSelector = skiplink.selector;
    let found = findSkiplink(usedSelector);

    if (!found && skiplink.secondarySelector) {
      usedSelector = skiplink.secondarySelector;
      found = findSkiplink(usedSelector);
    }

    if (!found) continue;

    existingSkipLinks.push({
      element: found,
      selector: usedSelector,
      title: skiplink.title,
    });
  }

  if (existingSkipLinks.length > 0) {
    const skipLinkComponent = document.createElement("oc-skip-link-v1");
    skipLinkComponent.classList.add(INJECTED_SKIPLINKS_CLASS);
    skipLinkComponent.setAttribute("variant", "header");

    for (const skiplink of existingSkipLinks) {
      const buttonElement = document.createElement("oc-button-v1");
      buttonElement.textContent = skiplink.title;
      buttonElement.dataset.ocSkipLinkV1To = skiplink.selector;
      skipLinkComponent.appendChild(buttonElement);
    }

    document.body.prepend(skipLinkComponent);
  }
};

export const initializeHeaderSkiplinks = (): void => {
  injectHeaderSkiplinks();

  /*                                                                                         */
  on(window, "resize", () => {
    injectHeaderSkiplinks(true);
  });
};
